import { ActionIcon, NumberInput, Select, Tooltip } from '@mantine/core';
import { X } from '@phosphor-icons/react';
import React from 'react';

import { formatLeadTime } from '@api/vendors';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import SaveState, { type SaveStatus } from '@components/ui/SaveState';

import { FULL_WIDTH_DROPDOWN, type MakerDraft, type SelectOption } from './makerOptions';
import styles from './MakerRows.module.css';

/**
 * One vendor row as the mapping grid returns it: the relation, its terms, and the current
 * price. `term` is the relation's own whole number: a maker's MOQ, a supplier's priority.
 */
export interface SavedMaker {
    rowId: string;
    vendorId: string;
    vendorName: string;
    price: string | null;
    currency: string | null;
    term: string | null;
    /** The vendor's lane-derived lead time in days. Read-only here: it is set on the lanes. */
    leadTime: { min: number | null; max: number | null } | null;
    priceSince: string | null;
    priceHistory: string | null;
}

/** The words that differ between a product's makers and a material's suppliers. */
export interface MakerRowsLabels {
    /** Column header for the vendor, e.g. "Maker". */
    vendor: string;
    /** Column header for the whole-number term, e.g. "MOQ" or "Priority". */
    term: string;
    /** The action that records a new dated price, e.g. "Set new price". */
    setPrice: string;
    /** Shown when there are no rows at all. */
    empty: string;
    /** The add-row link, e.g. "+ Add maker". */
    add: string;
}

export const MAKER_LABELS: MakerRowsLabels = {
    vendor: 'Maker',
    term: 'MOQ',
    setPrice: 'Set new price',
    empty: 'No maker yet. Add one to raise production orders for this product.',
    add: '+ Add maker',
};

export const SUPPLIER_LABELS: MakerRowsLabels = {
    vendor: 'Supplier',
    term: 'Priority',
    setPrice: 'Set new cost',
    empty: 'No supplier yet. Add one to raise purchase orders for this material.',
    add: '+ Add supplier',
};

export interface MakerRowMeta {
    status?: SaveStatus;
    error?: string | null;
}

interface MakerRowsProps {
    makers: SavedMaker[];
    drafts: MakerDraft[];
    vendorOptions: SelectOption[];
    currencyOptions: string[];
    isAdmin: boolean;
    labels?: MakerRowsLabels;
    /** A saved row's currency or term changed; the page writes it at once. */
    onSavedChange: (maker: SavedMaker, patch: { currency?: string | null; term?: string }) => void;
    onSetPrice: (maker: SavedMaker) => void;
    onShowHistory: (maker: SavedMaker) => void;
    onRemove: (maker: SavedMaker) => void;
    savedMeta: (rowId: string) => MakerRowMeta | undefined;
    onDraftChange: (index: number, patch: Partial<MakerDraft>) => void;
    /** The draft's values have settled; with a vendor chosen it becomes a mapping. */
    onDraftCommit: (index: number) => void;
    onDraftRemove: (index: number) => void;
    draftMeta: (index: number) => MakerRowMeta | undefined;
    onAdd: () => void;
    onNewVendor?: () => void;
}

const PRICE_DECIMALS = 2;

/**
 * Who makes a product: the saved makers as a table, then any maker being added as a form row.
 *
 * Price is never typed into the table. It is a dated line in vendor_prices, so "Set new price"
 * opens the same form the Vendor Prices grid uses and the old price stays in the history. The
 * currency and MOQ are the relation's own and save as they change. Removing a maker is the
 * page's confirm, not the row's.
 */
const MakerRows: React.FC<MakerRowsProps> = ({
    makers,
    drafts,
    vendorOptions,
    currencyOptions,
    isAdmin,
    labels = MAKER_LABELS,
    onSavedChange,
    onSetPrice,
    onShowHistory,
    onRemove,
    savedMeta,
    onDraftChange,
    onDraftCommit,
    onDraftRemove,
    draftMeta,
    onAdd,
    onNewVendor,
}) => {
    const columns: DataSheetColumn<SavedMaker>[] = [
        {
            key: 'vendorName',
            header: labels.vendor,
            width: 220,
            render: (value: string, row) => (
                <div className={styles.vendorCell}>
                    <span>{value || row.vendorId}</span>
                    {value && value !== row.vendorId ? <span className={styles.vendorId}>{row.vendorId}</span> : null}
                    {savedMeta(row.rowId)?.status ? (
                        <SaveState status={savedMeta(row.rowId)!.status!} message={savedMeta(row.rowId)?.error} />
                    ) : null}
                </div>
            ),
        },
        {
            key: 'currency',
            header: 'Currency',
            width: 110,
            render: (value: string | null, row) => (
                <Select
                    size="xs"
                    data={currencyOptions}
                    value={value}
                    onChange={(v) => onSavedChange(row, { currency: v })}
                    placeholder="Choose"
                    clearable
                    searchable
                    comboboxProps={FULL_WIDTH_DROPDOWN}
                    aria-label={`Currency for ${row.vendorName || row.vendorId}`}
                />
            ),
        },
        {
            key: 'term',
            header: labels.term,
            width: 110,
            render: (value: string | null, row) => (
                <TermCell value={value} onCommit={(term) => onSavedChange(row, { term })} label={`${labels.term} for ${row.vendorName || row.vendorId}`} />
            ),
        },
        {
            key: 'leadTime',
            header: 'Lead time',
            width: 110,
            render: (_value, row) => (
                <Tooltip label="From the vendor's lanes; change it under Settings, Lanes" withArrow>
                    <span className={styles.leadTime}>{formatLeadTime(row.leadTime?.min, row.leadTime?.max)}</span>
                </Tooltip>
            ),
        },
        {
            key: 'price',
            header: 'Current price',
            width: 220,
            render: (value: string | null, row) => (
                <div className={styles.priceCell}>
                    <span className={value ? styles.price : styles.noPrice}>
                        {value ? `${value} ${row.currency ?? ''}`.trim() : 'No price agreed'}
                    </span>
                    {row.priceSince && <span className={styles.since}>since {row.priceSince}</span>}
                    <span className={styles.priceActions}>
                        {isAdmin ? (
                            <button type="button" className={styles.linkButton} onClick={() => onSetPrice(row)}>
                                {labels.setPrice}
                            </button>
                        ) : (
                            <Tooltip label="Admins only" withArrow>
                                <span className={styles.locked}>{labels.setPrice}</span>
                            </Tooltip>
                        )}
                        {(row.priceHistory || value) && (
                            <button type="button" className={styles.linkButton} onClick={() => onShowHistory(row)}>
                                History
                            </button>
                        )}
                    </span>
                </div>
            ),
        },
        {
            key: '_remove',
            header: '',
            width: 60,
            render: (_value, row) =>
                isAdmin ? (
                    <Tooltip label="Remove maker" withArrow>
                        <ActionIcon variant="subtle" color="gray" aria-label={`Remove ${row.vendorName || row.vendorId}`} onClick={() => onRemove(row)}>
                            <X size={14} />
                        </ActionIcon>
                    </Tooltip>
                ) : null,
        },
    ];

    return (
        <div className={styles.rows}>
            {makers.length === 0 && drafts.length === 0 && <span className={styles.empty}>{labels.empty}</span>}
            {makers.length > 0 && (
                <DataSheet<SavedMaker> data={makers} columns={columns} getRowKey={(r) => r.rowId} bare hideHeaderBar maxHeight="auto" />
            )}
            {drafts.map((row, index) => {
                const m = draftMeta(index);
                return (
                    <div key={index}>
                        <div className={styles.row}>
                            <Select
                                label="Vendor"
                                placeholder="Choose a vendor"
                                data={vendorOptions}
                                value={row.vendorId}
                                onChange={(v) => {
                                    onDraftChange(index, { vendorId: v });
                                    onDraftCommit(index);
                                }}
                                searchable
                                comboboxProps={FULL_WIDTH_DROPDOWN}
                                size="xs"
                            />
                            <Tooltip label="Admins only" disabled={isAdmin} withArrow>
                                <div>
                                    <NumberInput
                                        label="Price per unit"
                                        placeholder="Optional"
                                        value={row.price}
                                        onChange={(v) => onDraftChange(index, { price: v === '' ? '' : Number(v) })}
                                        min={0}
                                        decimalScale={PRICE_DECIMALS}
                                        allowNegative={false}
                                        disabled={!isAdmin}
                                        size="xs"
                                        onBlur={() => onDraftCommit(index)}
                                    />
                                </div>
                            </Tooltip>
                            <Select
                                label="Currency"
                                placeholder="Choose"
                                data={currencyOptions}
                                value={row.currency}
                                onChange={(v) => {
                                    onDraftChange(index, { currency: v });
                                    onDraftCommit(index);
                                }}
                                clearable
                                searchable
                                size="xs"
                                comboboxProps={FULL_WIDTH_DROPDOWN}
                            />
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label="Remove maker"
                                className={styles.remove}
                                onClick={() => onDraftRemove(index)}
                            >
                                <X size={14} />
                            </ActionIcon>
                            {m?.status && (
                                <div className={styles.rowMeta}>
                                    <SaveState status={m.status} message={m.error} />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
            <div className={styles.actions}>
                <button type="button" className={styles.linkButton} onClick={onAdd}>
                    {labels.add}
                </button>
                {onNewVendor && (
                    <button type="button" className={styles.linkButton} onClick={onNewVendor}>
                        + New vendor
                    </button>
                )}
            </div>
        </div>
    );
};

/** A whole-number box that writes on blur; blank clears the term. */
const TermCell: React.FC<{ value: string | null; label: string; onCommit: (term: string) => void }> = ({ value, label, onCommit }) => {
    const [draft, setDraft] = React.useState<number | ''>(value == null || value === '' ? '' : Number(value));
    React.useEffect(() => {
        setDraft(value == null || value === '' ? '' : Number(value));
    }, [value]);
    return (
        <NumberInput
            size="xs"
            value={draft}
            onChange={(v) => setDraft(v === '' ? '' : Number(v))}
            onBlur={() => {
                const next = draft === '' ? '' : String(draft);
                if (next !== (value ?? '')) onCommit(next);
            }}
            min={0}
            allowDecimal={false}
            allowNegative={false}
            placeholder="None"
            aria-label={label}
        />
    );
};

export default MakerRows;
